/**
 * Team Settings — the Team Admin's tenant configuration surface.
 *
 * | Tab             | Ships                                                    | Req            |
 * |-----------------|----------------------------------------------------------|----------------|
 * | General         | team display name + portal facts                         | FR-TENANT-01   |
 * | Video           | provider dropdown w/ fan-visibility badges, Hudl URL,    | FR-TENANT-05,  |
 * |                 | per-event embeds (search/filter/sort/page + clear)       | FR-VIDEO-03..05|
 * | Support         | support-recipient email override                         | FR-HELP-07     |
 * | Success Measure | per-phase thresholds, versioned save                     | FR-PLAY-07     |
 *
 * The active tab lives in `?tab=`, so a refresh, a bookmark and a link shared
 * with a colleague all land on the same panel. The per-event embed query lives
 * in the URL too (see `use-video-settings.ts`).
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { PageHeader } from '../../components/page-header';
import { ApiError } from '../../lib/api-error';
import type { TenantSummary } from '../../lib/api-types';

import { GeneralSettings } from './general-settings';
import { SuccessMeasureSettings } from './success-measure-settings';
import { SupportEmailSettings } from './support-email-settings';
import { useVideoSettings } from './use-video-settings';
import { VideoProviderSettings } from './video-provider-settings';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'video', label: 'Video' },
  { id: 'support', label: 'Support' },
  { id: 'success-measure', label: 'Success Measure' },
] as const;

type TabId = (typeof TABS)[number]['id'];

function readTab(raw: string | null): TabId {
  const match = TABS.find((tab) => tab.id === raw);
  return match?.id ?? 'general';
}

export function TeamSettings(): JSX.Element {
  const apiClient = useApiClient();
  const { claims, session } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = readTab(searchParams.get('tab'));

  const tenantId = claims?.tid ?? session?.tenantId ?? null;
  const [tenant, setTenant] = useState<TenantSummary | null>(null);
  const [isTenantLoading, setIsTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const videoController = useVideoSettings();

  useEffect(() => {
    if (tenantId === null) {
      setIsTenantLoading(false);
      return undefined;
    }
    let isActive = true;
    setIsTenantLoading(true);
    apiClient
      .getTenant(tenantId)
      .then((loaded) => {
        if (!isActive) return;
        setTenant(loaded);
        setTenantError(null);
      })
      .catch((caught: unknown) => {
        if (!isActive) return;
        setTenantError(
          ApiError.isApiError(caught) ? caught.message : 'Team details could not be loaded.'
        );
      })
      .finally(() => {
        if (isActive) setIsTenantLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [apiClient, tenantId, reloadToken]);

  const setTab = useCallback(
    (next: TabId) => {
      const params = new URLSearchParams(searchParams);
      if (next === 'general') params.delete('tab');
      else params.set('tab', next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const saveTenant = useCallback(
    async (patch: { name?: string; support_recipient_email?: string | null }): Promise<void> => {
      if (tenantId === null) return;
      const updated = await apiClient.updateTenant(tenantId, patch);
      setTenant(updated);
    },
    [apiClient, tenantId]
  );

  if (tenantId === null) {
    return (
      <section className="team-settings">
        <div className="banner banner--error" role="alert">
          <p>
            This account is not attached to a team, so there are no team settings to manage. Sign in
            through your team portal.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="team-settings">
      <PageHeader
        className="team-settings__header"
        title="Team settings"
        context="Team identity, live video, support routing and the Success Measure rules behind your success rates."
      />

      <nav className="team-settings__tabs" aria-label="Team settings sections">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className="team-settings__tab"
            aria-current={tab === entry.id ? 'page' : undefined}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      {tab === 'general' ? (
        <GeneralSettings
          tenant={tenant}
          isLoading={isTenantLoading}
          error={tenantError}
          onRetry={() => setReloadToken((token) => token + 1)}
          onSave={saveTenant}
        />
      ) : null}

      {tab === 'video' ? <VideoProviderSettings controller={videoController} /> : null}

      {tab === 'support' ? (
        <SupportEmailSettings
          tenant={tenant}
          isLoading={isTenantLoading}
          error={tenantError}
          onRetry={() => setReloadToken((token) => token + 1)}
          onSave={saveTenant}
        />
      ) : null}

      {tab === 'success-measure' ? <SuccessMeasureSettings /> : null}
    </section>
  );
}
